/* Native recently-viewed section, using the shared account/company-scoped history only. */
(function (win) {
  'use strict';
  const ID = /^[1-9][0-9]{0,24}$/;
  const MAX_ITEMS = 12;
  function nativeScope(value) {
    if (!value || typeof value !== 'object') return null;
    const { shop, customer, company, location } = value;
    if (typeof shop !== 'string' || !/^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?\.[a-z]{2,63}$/.test(shop) || shop.includes('..') || typeof customer !== 'string' || customer !== 'guest' && !ID.test(customer) || typeof company !== 'string' || company !== '' && !ID.test(company) || typeof location !== 'string' || location !== '' && !ID.test(location) || customer === 'guest' && (company || location)) return null;
    return { shop, customer, company, location };
  }
  function currentNativeScope(document) {
    try {
      const raw = document.getElementById('SaberRecommendationsContext')?.getAttribute('data-context');
      return typeof raw === 'string' && raw.length <= 20000 ? nativeScope(JSON.parse(raw)) : null;
    } catch (_) { return null; }
  }
  function selection(snapshot) {
    if (!snapshot || snapshot.ready !== true || typeof snapshot.scope !== 'string' || !snapshot.scope || snapshot.scope.length > 512 || !Array.isArray(snapshot.views)) return null;
    const current = typeof snapshot.currentProductId === 'string' && ID.test(snapshot.currentProductId) ? snapshot.currentProductId : '';
    const seen = new Set();
    const views = snapshot.views.slice(0, 1000).filter(item => item && typeof item.id === 'string' && ID.test(item.id) && item.id !== current && Number.isSafeInteger(item.viewedAt) && item.viewedAt > 0).sort((a, b) => b.viewedAt - a.viewedAt).filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id); return true;
    }).slice(0, MAX_ITEMS);
    return { scope: snapshot.scope, ids: views.map(item => item.id), key: JSON.stringify([snapshot.scope, current, views.map(item => [item.id, item.viewedAt])]) };
  }
  function searchUrl(route, sectionId, ids, origin) {
    if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//') || /[\\\s]|\.\.|%(?:2e|2f|5c)/i.test(route) || typeof sectionId !== 'string' || !/^[A-Za-z0-9_-]{1,150}$/.test(sectionId) || !Array.isArray(ids) || !ids.length || ids.length > MAX_ITEMS || ids.some(id => typeof id !== 'string' || !ID.test(id))) return null;
    try {
      const url = new URL(route, origin);
      if (url.origin !== new URL(origin).origin || url.username || url.password || url.search || url.hash || !/^\/(?:[A-Za-z]{2}(?:-[A-Za-z0-9]{2,8})*\/)?search$/.test(url.pathname)) return null;
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ids.map(id => 'id:' + id).join(' OR '));
      return url;
    } catch (_) { return null; }
  }
  if (typeof module === 'object' && module.exports) module.exports = { selection, searchUrl, nativeScope };
  if (!win || !win.document || !win.customElements || win.customElements.get('recently-viewed-products')) return;

  class RecentlyViewedProducts extends win.HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.epoch = 0;
      this.selectionKey = '';
      this.hide();
      this.lifetime = new AbortController();
      const on = (target, name, fn) => target.addEventListener(name, fn, { signal: this.lifetime.signal });
      on(win.document, 'saber:viewed-history-ready', () => this.refresh());
      on(win.document, 'saber:viewed-history-change', () => this.refresh());
      on(win, 'pageshow', event => this.refresh(event.persisted === true));
      on(this, 'click', event => {
        const button = event.target.closest('[data-saber-viewed-remove]');
        if (!button || !this.contains(button) || !ID.test(button.dataset.saberViewedRemove || '')) return;
        event.preventDefault();
        try { this.history?.remove?.(button.dataset.saberViewedRemove); } catch (_) { /* The section remains useful if removal is unavailable. */ }
        this.refresh();
      });
      this.refresh();
    }
    disconnectedCallback() {
      this.active = false;
      this.epoch++;
      this.fetchAbort?.abort();
      this.lifetime?.abort();
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.destroyCarousels();
    }
    destroyCarousels() {
      this.querySelectorAll('.grid-carousel').forEach(grid => {
        try { win.Flickity?.data?.(grid)?.destroy?.(); } catch (_) { /* Native carousel cleanup is best effort. */ }
      });
    }
    hide() {
      this.hidden = true;
      this.dataset.productNavState = 'empty';
      this.destroyCarousels();
      this.replaceChildren();
    }
    state() {
      const api = win.SaberViewedHistory;
      if (!api || typeof api.snapshot !== 'function') return null;
      if (api !== this.history) {
        this.unsubscribe?.();
        this.history = api;
        this.unsubscribe = typeof api.subscribe === 'function' ? api.subscribe(() => this.refresh()) : null;
      }
      try {
        const selected = selection(api.snapshot()), native = currentNativeScope(win.document);
        return selected && native ? { ...selected, native, key: JSON.stringify([selected.key, native]) } : null;
      } catch (_) { return null; }
    }
    async refresh(force = false) {
      if (!this.active) return;
      const state = this.state();
      if (!force && state && state.key === this.selectionKey) {
        try { this.history?.hydrate?.(this); } catch (_) { /* Missing badges do not block native products. */ }
        return;
      }
      this.selectionKey = state?.key || '';
      const epoch = ++this.epoch;
      this.fetchAbort?.abort();
      this.hide();
      if (!state?.ids.length) return;
      const url = searchUrl(win.routes?.search_url, this.dataset.sectionId, state.ids, win.location.origin);
      if (!url) return;
      if (this.hasAttribute('data-product-nav-target')) {
        const loading = win.document.createElement('p');
        loading.className = 'ss-product-nav-loading';
        loading.setAttribute('role', 'status');
        loading.textContent = 'Loading previously viewed products…';
        this.replaceChildren(loading);
        this.dataset.productNavState = 'pending';
        this.hidden = false;
      }
      const controller = new AbortController();
      this.fetchAbort = controller;
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await win.fetch(url.href, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers: { Accept: 'text/html' }, signal: controller.signal });
        if (!response.ok || response.redirected || !/^text\/html(?:\s*;|$)/i.test(response.headers.get('content-type') || '')) throw Error('Invalid native section response');
        if (response.url) {
          const returned = new URL(response.url);
          if (returned.origin !== url.origin || returned.pathname !== url.pathname) throw Error('Unexpected native section URL');
        }
        const length = response.headers.get('content-length');
        if (length && (!/^\d+$/.test(length) || Number(length) > 2 * 1024 * 1024)) throw Error('Section too large');
        const content = await response.text();
        if (content.length > 2 * 1024 * 1024) throw Error('Section too large');
        if (epoch !== this.epoch || !this.active || this.state()?.key !== state.key) return;
        const parsed = new win.DOMParser().parseFromString(content, 'text/html');
        const wrappers = parsed.querySelectorAll('recently-viewed-products');
        if (wrappers.length !== 1 || wrappers[0].dataset.sectionId !== this.dataset.sectionId) throw Error('Missing native section wrapper');
        const wrapper = wrappers[0];
        const returnedScope = nativeScope(Object.fromEntries(['shop', 'customer', 'company', 'location'].map(key => [key, wrapper.getAttribute('data-saber-viewed-' + key)])));
        if (!returnedScope || JSON.stringify(returnedScope) !== JSON.stringify(state.native)) throw Error('Native purchasing context changed');
        wrapper.querySelectorAll('script, style, link, iframe, object, embed, base, meta[http-equiv]').forEach(node => node.remove());
        wrapper.querySelectorAll('*').forEach(node => {
          for (const attr of [...node.attributes]) {
            if (/^on/i.test(attr.name) || ['href', 'src', 'action', 'formaction'].includes(attr.name) && /^\s*javascript:/i.test(attr.value)) node.removeAttribute(attr.name);
          }
        });
        const allowed = new Set(state.ids), seen = new Set(), nodes = new Map();
        wrapper.querySelectorAll('product-card').forEach(card => {
          const id = card.dataset.productId || '';
          const cell = card.closest('.grid-item') || card;
          if (!ID.test(id) || !allowed.has(id) || seen.has(id)) { cell.remove(); return; }
          seen.add(id); nodes.set(id, cell);
          const footer = card.querySelector('.card-footer');
          if (footer && typeof this.history?.remove === 'function') {
            const button = win.document.createElement('button');
            button.type = 'button';
            button.className = 'button-text-link card-product-recently-viewed-dismiss';
            button.dataset.saberViewedRemove = id;
            button.textContent = typeof win.recentlyViewedStrings?.remove === 'string' ? win.recentlyViewedStrings.remove : 'Remove';
            footer.append(button);
          }
        });
        for (const [id, node] of nodes) if (!wrapper.contains(node)) nodes.delete(id);
        if (!nodes.size) { this.hide(); return; }
        for (const id of state.ids) { const node = nodes.get(id); if (node?.parentNode) node.parentNode.append(node); }
        const fragment = win.document.createDocumentFragment();
        for (const child of [...wrapper.childNodes]) fragment.append(win.document.importNode(child, true));
        if (epoch !== this.epoch || this.state()?.key !== state.key) return;
        this.replaceChildren(fragment);
        this.dataset.productNavState = 'ready';
        this.hidden = false;
        try { this.history?.hydrate?.(this); } catch (_) { /* Native product data remains usable. */ }
        win.requestAnimationFrame(() => {
          if (!this.active || epoch !== this.epoch) return;
          this.querySelectorAll('.grid-carousel').forEach(grid => {
            try { const slider = win.Flickity?.data?.(grid); slider?.reloadCells?.(); slider?.resize?.(); } catch (_) { /* The native scroller still works. */ }
          });
          this.dispatchEvent(new win.CustomEvent('recently-viewed:rendered', { bubbles: true }));
        });
      } catch (_) {
        if (epoch === this.epoch && this.active) this.hide();
      } finally {
        clearTimeout(timer);
        if (this.fetchAbort === controller) this.fetchAbort = null;
      }
    }
  }
  win.customElements.define('recently-viewed-products', RecentlyViewedProducts);
})(typeof window === 'undefined' ? null : window);
